import { useQuery } from '@tanstack/react-query';
import {
	Cartesian3,
	Math as CesiumMath,
	CustomDataSource,
	DistanceDisplayCondition,
	defined,
	Entity,
	HeadingPitchRange,
	HeightReference,
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	VerticalOrigin,
} from 'cesium';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type MappedAssetSensor, mapAssetSensor } from '@/common/asset-sensor';
import { useCesium } from '@/context/CesiumContext';
import { useAssetSettings } from '@/hooks/use-asset-settings';
import { createCombinedSensorImage } from '@/lib/utils/cesiumSensorStyles';
import { useTheme } from '@/context/ThemeContext';
import { useQueryListAssetSensorsOptions } from '@/queries/assets/sensors/list-sensors';

interface SensorLoaderProps {
	assetId: string;
	selectedSensor: MappedAssetSensor | null;
	onSelectSensor: (sensor: MappedAssetSensor | null) => void;
}

const SensorLoader: React.FC<SensorLoaderProps> = ({ assetId, selectedSensor, onSelectSensor }) => {
	const { viewer, undergroundEnabled, registerSubSurfaceLayer, subSurfaceLayers } = useCesium();
	const { isDarkTheme } = useTheme();
	const { t, i18n } = useTranslation();

	const { getLayerVisibility } = useAssetSettings(assetId);

	const dataSourceRef = useRef<CustomDataSource | null>(null);

	const { data, isLoading } = useQuery(useQueryListAssetSensorsOptions(assetId));

	// Init DataSource
	useEffect(() => {
		if (!viewer) return;
		const ds = new CustomDataSource('sensors-layer');
		viewer.dataSources.add(ds);
		dataSourceRef.current = ds;
		return () => {
			if (!viewer.isDestroyed()) {
				viewer.dataSources.remove(ds);
				registerSubSurfaceLayer({ id: 'sensors-layer', name: '', type: 'datasource' }, 'remove');
			}
		};
	}, [viewer, registerSubSurfaceLayer]);

	// Render Entities & Update Context
	useEffect(() => {
		if (!viewer) return;
		if (!dataSourceRef.current || isLoading || !data?.sensors) return;

		const entities = dataSourceRef.current.entities;
		entities.removeAll();

		const sensors = data.sensors.map(mapAssetSensor).filter((s) => s.coordinates);
		const hasSensors = sensors.length > 0;

		// Register to Context
		if (hasSensors) {
			registerSubSurfaceLayer({ id: 'sensors-layer', name: t('viewer.layers.iotSensors'), type: 'datasource' }, 'add');
		} else {
			registerSubSurfaceLayer(
				{ id: 'sensors-layer', name: t('viewer.layers.iotSensors'), type: 'datasource' },
				'remove',
			);
		}

		sensors.forEach((sensor) => {
			if (!sensor.coordinates) return;
			const { lat, lng, height } = sensor.coordinates;
			const position = Cartesian3.fromDegrees(lng, lat, height);
			entities.add({
				id: sensor.uniqId.toString(),
				position,
				show: true,
				billboard: {
					image: createCombinedSensorImage(sensor, false, isDarkTheme) as any,
					verticalOrigin: VerticalOrigin.BOTTOM,
					heightReference: HeightReference.NONE,
					scale: 1,
					// sizeInMeters: false,
					// scaleByDistance: undefined,
					// eyeOffset: Cartesian3.ZERO,
					distanceDisplayCondition: new DistanceDisplayCondition(0.0, 6000.0),
					disableDepthTestDistance: Number.POSITIVE_INFINITY,
				},
				properties: { rawSensor: sensor },
			});
		});

		viewer.scene.requestRender();
		// Labels are baked into each sensor by mapAssetSensor, so the pins must be rebuilt on a language switch.
		// i18n.resolvedLanguage is not read directly; it only forces this effect to re-run.
		// `t` translates the layer name registered above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewer, data, i18n.resolvedLanguage, t]);

	// Handle Visibility Toggle
	useEffect(() => {
		if (!dataSourceRef.current) return;

		const ds = dataSourceRef.current;
		const layerId = 'sensors-layer';

		if (!undergroundEnabled) {
			ds.show = false;
		} else {
			// If underground is ON, show it unless user explicitly turned off this specific layer
			ds.show = getLayerVisibility(layerId, true);
		}

		viewer?.scene.requestRender();

		// If sensors are the only sub-surface layer, zoom when enabled
		if (
			viewer &&
			undergroundEnabled &&
			ds.entities.values.length > 0 &&
			subSurfaceLayers.length === 1 &&
			subSurfaceLayers[0].id === 'sensors-layer'
		) {
			viewer
				.flyTo(ds, {
					duration: 1.5,
					offset: new HeadingPitchRange(0.0, CesiumMath.toRadians(-45), 500),
				})
				.catch(console.warn);
		}
	}, [undergroundEnabled, viewer]);
	// Update Selection
	useEffect(() => {
		if (!viewer || !dataSourceRef.current) return;

		const ds = dataSourceRef.current;

		ds.entities.values.forEach((entity: Entity) => {
			const rawSensor = entity.properties?.rawSensor?.getValue();
			if (!rawSensor || !entity.billboard) return;

			const isSelected = selectedSensor?.id === rawSensor.id;

			entity.billboard.image = createCombinedSensorImage(rawSensor, isSelected, isDarkTheme) as any;
			entity.billboard.scale = isSelected ? 1.5 : 1.0;

			// entity.billboard.eyeOffset = isSelected ? new Cartesian3(0, 0, -10) : Cartesian3.ZERO;

			// entity.billboard.disableDepthTestDistance = isSelected
			// 	? Number.POSITIVE_INFINITY
			// 	: undefined;
		});

		viewer.scene.requestRender();
		// Labels are baked into each sensor by mapAssetSensor, so the selected pin's overlay must be
		// redrawn on a language switch (it reads statusSettings.label from the entity's stored sensor).
		// i18n.resolvedLanguage is not read directly; it only forces this effect to re-run.
	}, [selectedSensor, viewer, isDarkTheme, i18n.resolvedLanguage]);

	useEffect(() => {
		if (!viewer) return;
		const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
		handler.setInputAction((movement: any) => {
			const picked = viewer.scene.pick(movement.position);
			if (defined(picked) && picked.id?.properties?.rawSensor) {
				onSelectSensor(picked.id.properties.rawSensor.getValue());
				return;
			}
			// Clicking anything else (an IFC element, a damage, empty space) gives the
			// inspector slot up, the way a damage click already does.
			onSelectSensor(null);
		}, ScreenSpaceEventType.LEFT_CLICK);
		return () => {
			if (!handler.isDestroyed()) handler.destroy();
		};
	}, [viewer, onSelectSensor]);

	if (isLoading) return null;

	return null;
};

export default SensorLoader;
